import { DropdownEditor, Field, TextAreaEditor } from '@approvalmax/ui';
import { Spacing, Text, Tooltip } from '@approvalmax/ui/src/components';
import { useAtomValue } from 'jotai';
import { constants, statics } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector, useShallowEqualSelector } from 'modules/react-redux';
import { TwoDifferentValuesWarning } from 'modules/request';
import CurrencyExchangeRateInput from 'pages/requestForm/components/CurrencyExchangeRateInput/CurrencyExchangeRateInput';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { getCanCreateContact, getIsExchangeRateInputAvailable } from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useCallback, useEffect } from 'react';
import bemFactory from 'react-bem-factory';
import { useDispatch } from 'react-redux';
import { ocrIsLoadingState } from 'shared/states';

import {
    changeExchangeRateParams,
    changeQbooksMailingAddress,
    changeQbooksVendor,
    ExchangeRateParams,
    showQBooksContactCreationPopup,
    updateQBooksLineItemsWithCurrencyExchangeRate,
} from '../../../actions';
import { SectionContainer } from '../../../components/SectionContainer';
import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { getRequest, getShowValidationErrors } from '../../../selectors/pageSelectors';
import { VendorDataProvider } from '../dataProviders/VendorDataProvider';
import { defaultOnChangeFieldRequiredForMatching } from '../QBooksBillForm/QBooksBillForm.helpers';
import { QBooksCounterpartyDetails } from '../QBooksCounterpartyDetails/QBooksCounterpartyDetails';
import { messages } from './QBooksVendorSection.messages';
import {
    AddressField,
    Content,
    StyledCurrencyDisclaimer,
    StyledExchangeRateWithUpdate,
    VendorFieldContainer,
} from './QBooksVendorSection.styles';
import type { QBooksVendorSectionProps } from './QBooksVendorSection.types';

const qa = bemFactory.qa('reqf-qbooks-vendor-section');

export const QBooksVendorSection: FC<QBooksVendorSectionProps> = memo((props) => {
    const { onChange = defaultOnChangeFieldRequiredForMatching } = props;

    const dispatch = useDispatch();

    const {
        vendor,
        mailingAddress,
        showErrors,
        isBill,
        isPo,
        companyCurrency,
        requestCurrency,
        exchangeRateValue,
        request,
    } = useShallowEqualSelector((state) => {
        const request = getRequest<domain.QBooksPoRequest | domain.QBooksBillRequest>(state);
        const isPo = request.integrationCode === domain.IntegrationCode.QBooksPo;
        const isBill = request.integrationCode === domain.IntegrationCode.QBooksBill;
        const d = request.details;
        const vendorContact = d.vendor;

        return {
            vendor: vendorContact,
            mailingAddress: d.mailingAddress,
            showErrors: getShowValidationErrors(state),
            isPo,
            isBill,
            companyCurrency: request.companyCurrency,
            requestCurrency: request.currency,
            exchangeRateValue: request.exchangeRate,
            request,
        };
    });
    const ocrIsLoading = useAtomValue(ocrIsLoadingState);
    const isExchangeRateInputAvailable = getIsExchangeRateInputAvailable(request);
    const canCreateContact = useSelector((state) => getCanCreateContact(state, request));

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData } = useOcrData(request, ocrAttachment?.id);

    const showVendorWarning = ocrData?.document?.vendorIds && ocrData.document.vendorIds.length > 1;
    const selectedCurrency = vendor?.currency || statics.currency.findCurrencyByCode(requestCurrency) || null;
    const showCurrencyWarning = Boolean(
        ocrData?.document?.currencyCode && selectedCurrency && ocrData.document.currencyCode !== selectedCurrency.id
    );

    const onExchangeRateChange = useCallback(
        (params: ExchangeRateParams) => {
            dispatch(changeExchangeRateParams(params));
            dispatch(updateQBooksLineItemsWithCurrencyExchangeRate(params.exchangeRate ?? null));
        },
        [dispatch]
    );

    useEffect(() => {
        if (vendor?.currency?.id === companyCurrency) {
            onExchangeRateChange({
                exchangeRate: null,
                exchangeRateSource: null,
                exchangeRateDate: null,
            });
        }
    }, [vendor, companyCurrency, onExchangeRateChange]);

    const handleCreateVendor = useCallback(() => {
        dispatch(showQBooksContactCreationPopup());

        return Promise.resolve(null);
    }, [dispatch]);

    const handleChangeMailingAddress = useCallback(
        (value: string) => {
            dispatch(changeQbooksMailingAddress(value));
        },
        [dispatch]
    );

    const handleManualChangeExchangeRate = useCallback(
        (value?: number) => {
            if (value) {
                onExchangeRateChange({
                    exchangeRate: value,
                    exchangeRateSource: domain.ExchangeRateSource.Manual,
                    exchangeRateDate: null,
                });
            } else {
                onExchangeRateChange({
                    exchangeRate: null,
                    exchangeRateSource: domain.ExchangeRateSource.None,
                    exchangeRateDate: null,
                });
            }
        },
        [onExchangeRateChange]
    );

    const handleChange = useCallback(
        (value: domain.QBooksContact) => {
            onChange(() => {
                dispatch(changeQbooksVendor(value));
            }).catch(() => {});
        },
        [dispatch, onChange]
    );

    return (
        <SectionContainer qa={qa()} title={messages.sectionName}>
            <Content>
                <VendorFieldContainer>
                    <Field required title={messages.vendorFieldTitle}>
                        <Tooltip
                            disabled={!showVendorWarning}
                            placement='top'
                            width='max-content'
                            activator={
                                <VendorDataProvider>
                                    <DropdownEditor
                                        qa={qa('vendor-editor')}
                                        invalid={showErrors && !vendor}
                                        value={vendor}
                                        singleLinePerItem={false}
                                        disabled={ocrIsLoading}
                                        warning={showVendorWarning}
                                        placeholder={messages.vendorPlaceholder}
                                        canCreateItemFn={() => canCreateContact}
                                        createItemDisplayTextFn={() => messages.addNewVendor}
                                        onChange={handleChange}
                                        onCreateItem={handleCreateVendor}
                                    />
                                </VendorDataProvider>
                            }
                        >
                            <Text spacing='8' font='body' fontSize='small'>
                                {messages.multipleVendorsWarning}
                            </Text>
                        </Tooltip>
                    </Field>

                    <QBooksCounterpartyDetails counterparty={vendor} hideAddress={isPo} />

                    <Spacing height={32} />

                    {isExchangeRateInputAvailable && (
                        <Tooltip
                            disabled={!showCurrencyWarning}
                            width='max-content'
                            placement='top'
                            activator={
                                <CurrencyExchangeRateInput
                                    warning={showCurrencyWarning}
                                    selectedCurrency={selectedCurrency}
                                    defaultCurrency={statics.currency.findCurrencyByCode(companyCurrency) || null}
                                    exchangeRateValue={
                                        request.exchangeRateSource === domain.ExchangeRateSource.Manual
                                            ? exchangeRateValue
                                            : null
                                    }
                                    precision={7}
                                    onExchangeRateChange={handleManualChangeExchangeRate}
                                    disabled={ocrIsLoading}
                                />
                            }
                        >
                            <TwoDifferentValuesWarning
                                value1={selectedCurrency?.id}
                                value2={ocrData?.document?.currencyCode}
                                label1={messages.differentCurrencyWarningLabel1}
                                label2={messages.differentCurrencyWarningLabel2}
                                description={messages.differentCurrencyWarningDescription}
                            />
                        </Tooltip>
                    )}

                    {request.exchangeRateSource === domain.ExchangeRateSource.Manual ? (
                        <StyledCurrencyDisclaimer />
                    ) : (
                        <StyledExchangeRateWithUpdate
                            onChange={onExchangeRateChange}
                            requestId={request.id}
                            companyId={request.companyId}
                            date={request.details.date}
                            currency={request.currency}
                            companyCurrency={request.companyCurrency}
                        />
                    )}
                </VendorFieldContainer>

                {!isBill && (
                    <AddressField title={messages.mailingAddressFieldTitle}>
                        <TextAreaEditor
                            changeOnBlur
                            qa={qa('mailing-address-editor')}
                            minHeight={4}
                            maxHeight={12}
                            maxLength={constants.qBooksConstants.MAILING_ADDRESS_MAX_LENGTH}
                            disabled={ocrIsLoading}
                            value={mailingAddress}
                            onChange={handleChangeMailingAddress}
                            placeholder={messages.mailingAddressPlaceholder}
                        />
                    </AddressField>
                )}
            </Content>
        </SectionContainer>
    );
});

QBooksVendorSection.displayName = 'QBooksVendorSection';
